import classNames         from 'classnames';
import * as React         from 'react';
import { useTranslation } from 'react-i18next';

import Preloader from '../containers/preloader';

import SearchLine     from './components/SearchLine';
import SearchDoorItem from './components/SearchDoorItem';
import SearchInfoItem from './components/SearchInfoItem';
import SearchNewsItem from './components/SearchNewsItem';
import SearchPageItem from './components/SearchPageItem';

import { useSearch } from '../../providers/search';

const SearchInput = function() {
  const { t } = useTranslation();

  const search = useSearch();

  const timeOut = React.useRef(null);

  const [collapse, setCollapse]       = React.useState({ doors: true });
  const [searchInput, setSearchInput] = React.useState('');
  const [showSearch, setShowSearch]   = React.useState(false);

  const hasResults = React.useMemo(() => {
    return (
      Object
        .values(search.doors)
        .some(Boolean) ||
      Object
        .values(search.news)
        .some(Boolean) ||
      Object
        .values(search.information)
        .some(Boolean) ||
      Object
        .values(search.pages)
        .some(Boolean)
    );
  }, [search.doors, search.news, search.information, search.pages]);

  const collapseToggle    = React.useCallback((event, data) => {
    event.preventDefault();
    event.stopPropagation();

    if (collapse[data.section] !== data.state) {
      setCollapse({
        ...collapse,
        [`${data.section}`]: data.state,
      });
    }
  }, [collapse]);
  const handleSearchClose = React.useCallback(() => {
    setSearchInput('');

    search.searchClose();
  }, []);
  const keyDown           = React.useCallback((event) => {
    if (event.key === 'Escape') {
      search.searchClose();

      setSearchInput('');
    }
  }, []);
  const resetTimeOut      = React.useCallback(() => {
    if (timeOut.current) {
      clearTimeout(timeOut.current);
    }
  }, []);
  const searchItems       = React.useCallback((value) => {
    if (value.length >= 3) {
      timeOut.current = setTimeout(() => {
        search.search(value
          .toLowerCase()
          .escapeSearch(),
        );
      }, 4e2);
    } else if (!value.length) {
      search.searchReset();
    }
  }, []);

  React.useEffect(() => {
    document.addEventListener('keydown', keyDown);

    return () => {
      document.removeEventListener('keydown', keyDown);
    };
  }, []);
  React.useEffect(() => {
    setShowSearch(!hasResults && search.searched && !search.loading);
  }, [hasResults, search.searched, search.loading]);
  // Todo: verify necessity of this useEffect
  React.useEffect(() => {
    const flag = hasResults || showSearch;
    document.body.classList.toggle('fixed', flag);

    return () => {
      document.body.classList.remove('fixed', flag);
    };
  }, [hasResults, showSearch]);

  return (
    <div
      className={'container'}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();

        handleSearchClose();
      }}
    >
      <SearchLine
        reset={search.searchReset}
        resetTimeout={resetTimeOut}
        search={searchItems}
        searchClose={handleSearchClose}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        setShowSearch={setShowSearch}
      />
      {showSearch && <span className={'emptyResult'}>{t('query_is_false')}</span>}
      <div
        className={classNames('overlay', {
          show: hasResults || !!searchInput.length,
        })}
      >
        <div className={'wrapper'}>
          <div className={'result'}>
            {!showSearch && (
              <>
                <div
                  className={classNames('doors', {
                    collapsed: search.doors.length > 2 && collapse.doors,
                  })}
                >
                  {Object
                    .values(search.doors)
                    .map((door, i) => {
                      return (
                        <SearchDoorItem
                          key={i}
                          offers={door}
                        />
                      );
                    })
                  }
                  {search.doors.length > 2 && (
                    <div
                      className={'toggle'}
                      onClick={(event) => {
                        collapseToggle(event, {
                          section: 'doors',
                          state:   !collapse.doors,
                        });
                      }}
                    ><span className={'ic-arrow-bottom'} /></div>
                  )}
                </div>
                {Object
                  .values(search.news)
                  .map((item, index) => {
                    return (
                      <SearchNewsItem
                        key={index}
                        {...item}
                      />
                    );
                  })
                }
                {Object
                  .values(search.information)
                  .map((item, index) => {
                    return (
                      <SearchInfoItem
                        handleSearchClose={handleSearchClose}
                        key={index}
                        {...item}
                      />
                    );
                  })
                }
                {Object
                  .values(search.pages)
                  .map((item, index) => {
                    return (
                      <SearchPageItem
                        handleSearchClose={handleSearchClose}
                        key={index}
                        {...item}
                      />
                    );
                  })
                }
              </>
            )}
          </div>
        </div>
      </div>
      {search.loading && <Preloader />}
    </div>
  );
};

export default SearchInput;
